/** Domain-neutral helpers for the temporal-distribution view (per-entity event
 *  timelines). Currently consumed by Report Clustering's Temporal Distribution; kept
 *  generic (entities are just "a key with a list of event timestamps") so the same
 *  panel can later serve barriers, sources, etc. Pure functions — no React/chart deps. */

import type {TrendWindow} from '../components/shared/TrendWindowSelect';
import {TrendWindowBoundary} from './trendWindowBoundary';

export type Bucketed = 'month' | 'quarter' | 'year';

/** Sort options for the temporal-distribution panel. Defined here (not in the panel)
 *  so {@link makeTemporalEntityComparator} can reference it without a circular import —
 *  the panel imports the panel-facing component types FROM the util layer. */
export type TemporalSort = 'count' | 'rising-first' | 'most-accelerating' | 'falling-first';

/** Recent-vs-prior count trend for one entity. `ratio` = recent/prior;
 *  `+Infinity` when emerging (prior 0), `0` when winding down (recent 0),
 *  `NaN` when there's no signal (both windows empty / no window configured). */
export interface CountTrendStats {
    trend: 'rising' | 'falling' | 'stable' | null;
    recent: number;
    prior: number;
    ratio: number;
}

/** Classify an entity's event timestamps (ms) into a recent-vs-prior count trend
 *  using the shared 1.3× / 0.7× ratio thresholds. Returns the no-signal stats when
 *  `window` is null, fewer than 2 dated events, or both windows are empty. */
export const computeCountTrend = (events: number[], window: TrendWindowBoundary | null): CountTrendStats => {
    if (!window) return {trend: null, recent: 0, prior: 0, ratio: NaN};
    const {start, recentStart, end} = window;
    let recent = 0;
    let prior = 0;
    let dated = 0;
    for (const t of events) {
        if (isNaN(t)) continue;
        dated++;
        if (t >= recentStart && t <= end) recent++;
        else if (t >= start && t < recentStart) prior++;
    }
    if (dated < 2 || (recent === 0 && prior === 0)) return {trend: null, recent, prior, ratio: NaN};
    if (prior === 0) return {trend: 'rising', recent, prior, ratio: Infinity};
    if (recent === 0) return {trend: 'falling', recent, prior, ratio: 0};
    const ratio = recent / prior;
    const trend = ratio > 1.3 ? 'rising' : ratio < 0.7 ? 'falling' : 'stable';
    return {trend, recent, prior, ratio};
};

/** Human-readable trend stats for a tooltip — recent/prior counts plus ratio. Returns
 *  null when there's no signal (both windows empty or no trend window configured). */
export function formatTrendStats(stats: CountTrendStats | undefined, windowMode: TrendWindow): string | null {
    if (!stats || (stats.recent === 0 && stats.prior === 0)) return null;
    const ratioStr = !isFinite(stats.ratio)
        ? '∞'
        : stats.ratio === 0
            ? '0'
            : `${stats.ratio.toFixed(stats.ratio >= 10 ? 1 : 2)}×`;
    const labels = windowMode === 'midpoint'
        ? {recent: '2nd half', prior: '1st half'}
        : {recent: `last ${windowMode}mo`, prior: `prior ${windowMode}mo`};
    return `${stats.recent} (${labels.recent}) vs ${stats.prior} (${labels.prior}) · ${ratioStr}`;
}

/** Distribution-free trend verdict for a barrier/cluster's failure-rate over its whole
 *  observed history — the all-time companion to {@link computeCountTrend}'s windowed
 *  ratio. `verdict` is null when there are too few events to judge. */
export interface LaplaceTrend {
    verdict: 'deteriorating' | 'improving' | 'stable' | null;
    /** Laplace centroid statistic (z-like); NaN when verdict is null. */
    u: number;
    n: number;
}

/** Laplace centroid trend test over a point process — a non-parametric (no distribution
 *  fit) test for a monotonic trend in the rate of occurrence. Observed over
 *  `[windowStart, windowEnd]`, with event timestamps in ms:
 *    U = (mean(t − windowStart) − T/2) / (T · √(1/(12n))),  T = windowEnd − windowStart.
 *  U > +threshold → events cluster late → failure rate INCREASING ('deteriorating');
 *  U < −threshold → DECREASING ('improving'); otherwise 'stable'. Default threshold 1.96
 *  ≈ 5% two-sided. Returns a null verdict below `minEvents` (the test is noise otherwise).
 *  Uses the full observation window (not just first→last event) so a recent quiet spell
 *  correctly reads as improving. */
export const laplaceTrend = (
    events: number[],
    windowStart: number,
    windowEnd: number,
    threshold = 1.96,
    minEvents = 4,
): LaplaceTrend => {
    const ts = events.filter((t) => !isNaN(t) && t >= windowStart && t <= windowEnd);
    const n = ts.length;
    const T = windowEnd - windowStart;
    if (n < minEvents || T <= 0) return {verdict: null, u: NaN, n};
    const mean = ts.reduce((s, t) => s + (t - windowStart), 0) / n;
    const u = (mean - T / 2) / (T * Math.sqrt(1 / (12 * n)));
    const verdict = u > threshold ? 'deteriorating' : u < -threshold ? 'improving' : 'stable';
    return {verdict, u, n};
};

/** Bucket-start timestamp for a date at the given granularity. */
export function bucketStartOf(d: Date, granularity: Bucketed): number {
    const y = d.getFullYear();
    if (granularity === 'year') return new Date(y, 0, 1).getTime();
    if (granularity === 'quarter') return new Date(y, Math.floor(d.getMonth() / 3) * 3, 1).getTime();
    return new Date(y, d.getMonth(), 1).getTime();
}

/** Last millisecond of the bucket containing the date — used to align axis max. */
export function bucketEndOf(d: Date, granularity: Bucketed): number {
    const y = d.getFullYear();
    if (granularity === 'year') return new Date(y + 1, 0, 1).getTime() - 1;
    if (granularity === 'quarter') {
        const qStart = Math.floor(d.getMonth() / 3) * 3;
        return new Date(y, qStart + 3, 1).getTime() - 1;
    }
    return new Date(y, d.getMonth() + 1, 1).getTime() - 1;
}

/** Bucket-start timestamp of the bucket *after* the one starting at `ts`. */
export function nextBucketStart(ts: number, granularity: Bucketed): number {
    const d = new Date(ts);
    if (granularity === 'year') return new Date(d.getFullYear() + 1, 0, 1).getTime();
    if (granularity === 'quarter') return new Date(d.getFullYear(), d.getMonth() + 3, 1).getTime();
    return new Date(d.getFullYear(), d.getMonth() + 1, 1).getTime();
}

/** Human-readable bucket label, e.g. "Q1 2008" / "2008" / "Mar 2008", from any timestamp in the bucket. */
export function bucketLabelOf(ts: number, granularity: Bucketed): string {
    const d = new Date(ts);
    const y = d.getFullYear();
    if (granularity === 'year') return String(y);
    if (granularity === 'quarter') return `Q${Math.floor(d.getMonth() / 3) + 1} ${y}`;
    return d.toLocaleDateString('en-US', {year: 'numeric', month: 'short'});
}

/** Minimal shape the comparator needs from an entity: a count and its count trend. */
interface CountTrendComparable {
    count: number;
    trend: CountTrendStats;
}

/** Build a comparator for the temporal-distribution panel's sort options, operating on
 *  any entity exposing a `count` and a `trend`. Ported from Report Clustering's
 *  `temporalSortComparator`, with one change: `'count'` actively sorts by count DESC
 *  (RC short-circuited to its pre-count-sorted array; callers here have no pre-sort).
 *
 *   - `'count'`            → count desc.
 *   - `'most-accelerating'`→ rising-trend entities first (by ratio desc, ∞ on top);
 *                            everything else falls below, ordered by count desc.
 *   - `'rising-first'` /
 *     `'falling-first'`    → trend-bucket order, count-desc tiebreak; null trend last. */
export function makeTemporalEntityComparator(
    sort: TemporalSort,
): (a: CountTrendComparable, b: CountTrendComparable) => number {
    if (sort === 'count') {
        return (a, b): number => b.count - a.count;
    }
    if (sort === 'most-accelerating') {
        return (a, b): number => {
            const aRising = a.trend?.trend === 'rising';
            const bRising = b.trend?.trend === 'rising';
            if (aRising !== bRising) return aRising ? -1 : 1;
            if (!aRising) return b.count - a.count;
            const ra = a.trend?.ratio ?? NaN;
            const rb = b.trend?.ratio ?? NaN;
            if (ra === rb) return b.count - a.count;
            return rb - ra;
        };
    }
    const order: Record<string, number> = sort === 'rising-first'
        ? {rising: 0, stable: 1, falling: 2}
        : {falling: 0, stable: 1, rising: 2};
    return (a, b): number => {
        const ta = a.trend?.trend ?? null;
        const tb = b.trend?.trend ?? null;
        const ra = ta == null ? 3 : (order[ta] ?? 3);
        const rb = tb == null ? 3 : (order[tb] ?? 3);
        if (ra !== rb) return ra - rb;
        return b.count - a.count;
    };
}
